import { useMemo } from 'react';
import { useGetPickemGoogleAd } from './useTenant';
import { useTenant } from '@/contexts/TenantContext';
import { deriveDivIdPrefix } from '@/config/ads.config';
import type { GoogleAdsPlacement } from './useTenant';

/**
 * Default sizes for the in-play sticky footer banner:
 * 320x50 on mobile, 728x90 on desktop.
 */
const STICKY_FOOTER_DEFAULT_SIZES: [number, number][] = [
  [320, 50],
  [728, 90],
];

/**
 * Hook to fetch and transform the per-pickem sticky footer ad placement.
 * Mirrors usePickemContestCardAd but addresses the PICKEM_STICKY_FOOTER key.
 * @param pickemId - The pickem ID
 * @returns Object with transformed ad placement and whether to show the ad
 */
export const usePickemStickyFooterAd = (pickemId: string | null | undefined) => {
  const { isAdsEnabled } = useTenant();
  const { data: pickemAdPlacement } = useGetPickemGoogleAd(pickemId, 'PICKEM_STICKY_FOOTER');

  // Transform pickem ad placement to AdPlacement format
  const transformedAdPlacement = useMemo((): GoogleAdsPlacement | null => {
    if (!pickemAdPlacement || !pickemAdPlacement.enabled || !pickemAdPlacement.adUnitPath) {
      return null;
    }

    const divIdPrefix = deriveDivIdPrefix(pickemAdPlacement as GoogleAdsPlacement);

    if (!divIdPrefix) {
      return null;
    }

    // Filter out empty targeting values
    const targeting = pickemAdPlacement.targeting
      ? Object.fromEntries(
          Object.entries(pickemAdPlacement.targeting).filter(
            ([_, value]) => value && value.trim() !== ''
          )
        )
      : undefined;

    return {
      placementKey: 'PICKEM_STICKY_FOOTER',
      enabled: pickemAdPlacement.enabled,
      adUnitPath: pickemAdPlacement.adUnitPath,
      sizes: pickemAdPlacement.sizes || STICKY_FOOTER_DEFAULT_SIZES,
      sizeMappings: pickemAdPlacement.sizeMappings,
      divIdPrefix,
      targeting,
    };
  }, [pickemAdPlacement]);

  const shouldShowAd = isAdsEnabled && transformedAdPlacement && transformedAdPlacement.enabled;

  return {
    adPlacement: transformedAdPlacement,
    shouldShowAd,
  };
};
